import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Pagination, Row, Spinner, Table } from "react-bootstrap";
import Layout from "../../layout/Layout";
import { getBoruUretimKalinlikAnalizDemo } from "../../../api/uretim/makineDataApi";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const MACHINE_COLORS = {
  1: "#1f77b4",
  2: "#2ca02c",
  3: "#ff7f0e",
  4: "#d62728",
  default: "#6c757d",
};

function formatNumber(value, digits = 0) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) return "-";

  return parsed.toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDateValue(value) {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("tr-TR", {
    timeZone: "Europe/Istanbul",
  });
}

function MakinaOrtalamaKalinlikAnaliz() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [makinaFilter, setMakinaFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [machineSummary, setMachineSummary] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const limit = 100;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await getBoruUretimKalinlikAnalizDemo(
        makinaFilter,
        page,
        limit,
        {
          month: monthFilter || undefined,
          startDate: startDateFilter || undefined,
          endDate: endDateFilter || undefined,
        }
      );

      const payload = res.data?.data || {};

      setRows(Array.isArray(payload.data) ? payload.data : []);
      setSummary(payload.summary || null);
      setMachineSummary(
        Array.isArray(payload.machineSummary) ? payload.machineSummary : []
      );
      setMonthlySummary(
        Array.isArray(payload.monthlySummary) ? payload.monthlySummary : []
      );
      setPaginationInfo(
        payload.pagination || {
          page,
          totalPages: 1,
          totalRecords: 0,
        }
      );
    } catch (err) {
      console.error("Kalınlık analiz verisi alınamadı:", err);
      setRows([]);
      setSummary(null);
      setMachineSummary([]);
      setMonthlySummary([]);
      setPaginationInfo({ page: 1, totalPages: 1, totalRecords: 0 });
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.msg ||
          "Kalınlık analiz verisi alınamadı."
      );
    } finally {
      setLoading(false);
    }
  }, [makinaFilter, page, monthFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const machineCards = useMemo(
    () =>
      [...machineSummary].sort(
        (a, b) => b.totalThicknessContribution - a.totalThicknessContribution
      ),
    [machineSummary]
  );

  const insightData = useMemo(() => {
    const bestMachine = machineCards[0] || null;
    const weakestMachine = machineCards[machineCards.length - 1] || null;
    const highestProductionMachine =
      [...machineSummary].sort((a, b) => b.totalProduction - a.totalProduction)[0] ||
      null;

    const chronologicalMonths = [...monthlySummary]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((item) => ({
        ...item,
        name: item.label,
      }));

    const lastMonth = chronologicalMonths[chronologicalMonths.length - 1] || null;
    const previousMonth =
      chronologicalMonths[chronologicalMonths.length - 2] || null;
    const trendDelta =
      lastMonth && previousMonth
        ? lastMonth.totalThicknessContribution -
          previousMonth.totalThicknessContribution
        : 0;

    return {
      bestMachine,
      weakestMachine,
      highestProductionMachine,
      lastMonth,
      trendDelta,
      chronologicalMonths,
    };
  }, [machineCards, machineSummary, monthlySummary]);

  const monthlyMachineChartData = useMemo(() => {
    return insightData.chronologicalMonths.map((item) => {
      const point = {
        name: item.label,
        overallContribution: item.totalThicknessContribution,
        overallThickness: item.weightedAverageThickness,
      };

      item.machines?.forEach((machine) => {
        point[`machine${machine.makinaAdi}Contribution`] =
          machine.totalThicknessContribution;
        point[`machine${machine.makinaAdi}Thickness`] =
          machine.weightedAverageThickness;
      });

      return point;
    });
  }, [insightData.chronologicalMonths]);

  const machineComparisonChartData = useMemo(
    () =>
      machineSummary.map((item) => ({
        name: `Makina ${item.makinaAdi}`,
        contribution: item.totalThicknessContribution,
        thickness: item.weightedAverageThickness,
        production: item.totalProduction,
      })),
    [machineSummary]
  );

  const filteredMachineLines =
    makinaFilter === "all"
      ? ["1", "2", "3", "4"]
      : [String(makinaFilter)];

  return (
    <Layout>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Ortalama Kalınlık Analizi</h2>
          <div className="text-muted small">
            Kaynak: `BoruProfilUretimDemo` • Hesaplanan kalınlık =
            mamül miktarı / 7.85 / bant eni / metraj x 1000
          </div>
          <div className="text-muted small">
            Tarih filtreleri ve aylık analiz `tarih` alanına göre çalışır.
            `Rapor Tarihi` ayrıca tabloda ayrı gösterilir.
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <Form.Select
            value={makinaFilter}
            onChange={(e) => {
              setMakinaFilter(e.target.value);
              setPage(1);
            }}
            style={{ width: 180 }}
          >
            <option value="all">Tüm Makineler</option>
            <option value="1">Makina 1</option>
            <option value="2">Makina 2</option>
            <option value="3">Makina 3</option>
            <option value="4">Makina 4</option>
          </Form.Select>

          <Form.Control
            type="month"
            value={monthFilter}
            onChange={(e) => {
              setMonthFilter(e.target.value);
              setPage(1);
            }}
            style={{ width: 150 }}
          />

          <Form.Control
            type="date"
            value={startDateFilter}
            onChange={(e) => {
              setStartDateFilter(e.target.value);
              setPage(1);
            }}
            style={{ width: 150 }}
          />

          <Form.Control
            type="date"
            value={endDateFilter}
            onChange={(e) => {
              setEndDateFilter(e.target.value);
              setPage(1);
            }}
            style={{ width: 150 }}
          />

          <Button
            variant="outline-secondary"
            onClick={() => {
              setMonthFilter("");
              setStartDateFilter("");
              setEndDateFilter("");
              setPage(1);
            }}
          >
            Temizle
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">Ort. Hesaplanan Kalınlık</div>
              <div className="fs-3 fw-bold">
                {formatNumber(summary?.weightedAverageThickness, 3)} mm
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-4">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">Toplam Üretim x Kalınlık</div>
              <div className="fs-3 fw-bold">
                {formatNumber(summary?.totalThicknessContribution, 2)}
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-4">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">Kayıt Sayısı</div>
              <div className="fs-3 fw-bold">
                {formatNumber(summary?.recordCount, 0)}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">En Güçlü Makina</div>
              <div className="fs-5 fw-bold">
                {insightData.bestMachine
                  ? `Makina ${insightData.bestMachine.makinaAdi}`
                  : "-"}
              </div>
              <div className="text-muted small mt-2">
                Katkı:{" "}
                {formatNumber(
                  insightData.bestMachine?.totalThicknessContribution,
                  2
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">En Yüksek Üretim</div>
              <div className="fs-5 fw-bold">
                {insightData.highestProductionMachine
                  ? `Makina ${insightData.highestProductionMachine.makinaAdi}`
                  : "-"}
              </div>
              <div className="text-muted small mt-2">
                Üretim:{" "}
                {formatNumber(
                  insightData.highestProductionMachine?.totalProduction,
                  2
                )}{" "}
                KG
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="text-muted small mb-2">Son Aylık Trend</div>
              <div
                className={`fs-5 fw-bold ${
                  insightData.trendDelta >= 0 ? "text-success" : "text-danger"
                }`}
              >
                {insightData.lastMonth
                  ? `${insightData.trendDelta >= 0 ? "+" : ""}${formatNumber(
                      insightData.trendDelta,
                      2
                    )}`
                  : "-"}
              </div>
              <div className="text-muted small mt-2">
                Son dönem karşılaştırması
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="mb-3">
                <h5 className="fw-bold mb-1">Aylık Trend Analizi</h5>
                <div className="text-muted small">
                  Genel katkı ve makina bazlı katkılar aynı grafikte izlenir.
                </div>
              </div>

              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={monthlyMachineChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="overallContribution"
                    name="Genel Katkı"
                    stroke="#111827"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                  />
                  {filteredMachineLines.map((machineNo) => (
                    <Line
                      key={machineNo}
                      type="monotone"
                      dataKey={`machine${machineNo}Contribution`}
                      name={`Makina ${machineNo}`}
                      stroke={MACHINE_COLORS[machineNo] || MACHINE_COLORS.default}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="mb-3">
                <h5 className="fw-bold mb-1">Makina Karşılaştırması</h5>
                <div className="text-muted small">
                  Hangi makinenin katkısı daha yüksek hızlıca görülür.
                </div>
              </div>

              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={machineComparisonChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="contribution" name="Üretim x Kalınlık" fill="#0d6efd" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="mb-3">
                <h5 className="fw-bold mb-1">Makina Bazlı Kalınlık Profili</h5>
                <div className="text-muted small">
                  Ağırlıklı ortalama kalınlık seviyeleri makineler arasında
                  dengeli mi görülür.
                </div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={machineComparisonChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar
                    dataKey="thickness"
                    name="Ağırlıklı Ort. Kalınlık"
                    fill="#20c997"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <div className="mb-3">
                <h5 className="fw-bold mb-1">Aylık Üretim Yoğunluğu</h5>
                <div className="text-muted small">
                  Üretim miktarı ile katkıyı birlikte okuyup anomalileri yakalamak
                  için.
                </div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyMachineChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar
                    dataKey="overallThickness"
                    name="Ort. Kalınlık"
                    fill="#f59e0b"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <div className="mb-3">
            <h5 className="fw-bold mb-1">Makina Bazlı Yönetim Özeti</h5>
            <div className="text-muted small">
              Operasyon toplantısında direkt kullanılabilecek özet tablo.
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <Table bordered hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Makina</th>
                  <th>Kayıt</th>
                  <th>Toplam Üretim (KG)</th>
                  <th>Ağırlıklı Ort. Kalınlık (mm)</th>
                  <th>Toplam Üretim x Kalınlık</th>
                </tr>
              </thead>
              <tbody>
                {machineCards.length > 0 ? (
                  machineCards.map((item) => (
                    <tr key={item.makinaAdi}>
                      <td>{`Makina ${item.makinaAdi}`}</td>
                      <td>{formatNumber(item.recordCount, 0)}</td>
                      <td>{formatNumber(item.totalProduction, 2)}</td>
                      <td>{formatNumber(item.weightedAverageThickness, 3)}</td>
                      <td>{formatNumber(item.totalThicknessContribution, 2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Makina özeti için veri bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="fw-bold mb-1">Ay Bazlı Görünüm</h5>
              <div className="text-muted small">
                Tarih aralığı verilirse ay filtresi yerine tarih aralığı baz alınır.
              </div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <Table bordered hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Ay</th>
                  <th>Kayıt</th>
                  <th>Toplam Üretim (KG)</th>
                  <th>Ağırlıklı Ort. Kalınlık (mm)</th>
                  <th>Toplam Üretim x Kalınlık</th>
                </tr>
              </thead>
              <tbody>
                {monthlySummary.length > 0 ? (
                  monthlySummary.map((item) => (
                    <tr key={item.key}>
                      <td>{item.label}</td>
                      <td>{formatNumber(item.recordCount, 0)}</td>
                      <td>{formatNumber(item.totalProduction, 2)}</td>
                      <td>{formatNumber(item.weightedAverageThickness, 3)}</td>
                      <td>{formatNumber(item.totalThicknessContribution, 2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Gösterilecek ay bazlı veri bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="fw-bold mb-1">Detay Kayıtlar</h5>
              <div className="text-muted small">
                Satır formülü:
                ` kalınlık = mamül miktarı / 7.85 / bant eni / metraj x 1000 `
                ardından ` toplam üretim x kalınlık `
              </div>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <Table bordered hover responsive className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Üretim Tarihi</th>
                      <th>Rapor Tarihi</th>
                      <th>Makina</th>
                      <th>Form No</th>
                      <th>İş Emri No</th>
                      <th>Operatör</th>
                      <th>Mamül KG</th>
                      <th>Bant Eni</th>
                      <th>Metraj</th>
                      <th>Hesaplanan Kalınlık (mm)</th>
                      <th>Toplam Üretim (KG)</th>
                      <th>Toplam Üretim x Kalınlık</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((row, index) => (
                        <tr key={row._id || `${row.formNo}-${index}`}>
                          <td>{(page - 1) * limit + index + 1}</td>
                          <td>{formatDateValue(row.tarih)}</td>
                          <td>{formatDateValue(row.raporTarihi)}</td>
                          <td>{row.makinaAdi || "-"}</td>
                          <td>{row.formNo || "-"}</td>
                          <td>{row.isEmriNo || "-"}</td>
                          <td>{row.operator || "-"}</td>
                          <td>{formatNumber(row.formulaInputs?.mamulMiktariKg, 2)}</td>
                          <td>{formatNumber(row.formulaInputs?.bantEni, 2)}</td>
                          <td>{formatNumber(row.formulaInputs?.metraj, 2)}</td>
                          <td>{formatNumber(row.calculatedThickness, 3)}</td>
                          <td>{formatNumber(row.formulaInputs?.toplamUretim, 2)}</td>
                          <td>{formatNumber(row.thicknessContribution, 2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={13} className="text-center text-muted py-4">
                          Filtreye uygun kayıt bulunamadı.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Sayfa {paginationInfo.page} / {paginationInfo.totalPages} •
                  Toplam {` `}
                  {formatNumber(paginationInfo.totalRecords, 0)} kayıt
                </small>

                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={page === 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  />
                  <Pagination.Item active>{page}</Pagination.Item>
                  <Pagination.Next
                    disabled={page >= paginationInfo.totalPages}
                    onClick={() =>
                      setPage((prev) =>
                        Math.min(paginationInfo.totalPages, prev + 1)
                      )
                    }
                  />
                </Pagination>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Layout>
  );
}

export default MakinaOrtalamaKalinlikAnaliz;
